import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const REGIONS = {
  us: {
    iroh: "visibility.amp.cisco.com",
    urlPattern: "https://(visibility|private\\.intel)\\.amp\\.cisco\\.com/.*",
  },
  eu: {
    iroh: "visibility.eu.amp.cisco.com",
    urlPattern:
      "https://(visibility|private\\.intel)\\.eu\\.amp\\.cisco\\.com/.*",
  },
  apjc: {
    iroh: "visibility.apjc.amp.cisco.com",
    urlPattern:
      "https://(visibility|private\\.intel)\\.apjc\\.amp\\.cisco\\.com/.*",
  },
} as const;

const region = (cfg: Record<string, string>) =>
  REGIONS[cfg.region as keyof typeof REGIONS] ?? REGIONS.us;

export const ciscoXdr = defineConnector({
  identifier: "ciscoXdr",
  name: "Cisco XDR",
  tagLine: "Extended detection and response",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: (cfg) => region(cfg).urlPattern,
  auth: oauthAuth,
  steps: [
    {
      title: "Create an API client in Cisco XDR",
      items: [
        {
          kind: "markdown",
          content:
            "In Cisco XDR, go to **Administration > API Clients** and click **Generate API Client**. Give it a name, pick the scopes your integration needs, and choose the **Client Credentials** grant. Copy the **Client ID** and **Client Password** — the password is shown only once.",
        },
      ],
    },
    {
      title: "Enter your API credentials",
      items: [
        {
          kind: "select",
          label: "Region",
          required: true,
          defaultValue: "us",
          helperText:
            "The region your Cisco XDR organization is hosted in. Credentials are valid in one region only.",
          target: { kind: "configuration", field: "region" },
          options: [
            { value: "us", label: "North America" },
            { value: "eu", label: "Europe" },
            { value: "apjc", label: "Asia Pacific (APJC)" },
          ],
        },
        {
          kind: "input",
          label: "Client ID",
          placeholder: "client-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client password",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    clientCredentialsFrom: "db",
    tokenEndpoint: (cfg) => `https://${region(cfg).iroh}/iroh/oauth2/token`,
    tokenEndpointAuthMethod: () => "client_secret_basic",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: (cfg) => `https://${region(cfg).iroh}/iroh/profile/whoami`,
  },
});
